import React, { useState, useEffect, useRef, useCallback } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import "../styles/components/partner.css";
import { PageLoader } from "../components/page-loader";
import livePrices from "../data/prices.live.json";
import testPrices from "../data/prices.test.json";

const priceData =
  process.env.REACT_APP_STRIPE_PRICES === "test" ? testPrices : livePrices;

const UserDomainsPartner = () => {
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [deletingDomains, setDeletingDomains] = useState({});
  const [removingPartners, setRemovingPartners] = useState({});
  const [accessToken, setAccessToken] = useState(null);
  const [userEmail, setUserEmail] = useState(null);
  const [showSettingsMenu, setShowSettingsMenu] = useState({});
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(null);
  const [showUpgradePopup, setShowUpgradePopup] = useState(null);
  const [showUpgradeConfirmation, setShowUpgradeConfirmation] = useState(null);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [upgradingDomains, setUpgradingDomains] = useState({});
  const isDeletingRef = useRef(false);

  const packages = priceData.upgradePackages;
  const { isAuthenticated, getAccessTokenSilently, user } = useAuth0();
  const API_URL = process.env.REACT_APP_API_BASE_URL;
  const [showSharePopup, setShowSharePopup] = useState(null);
  const [shareEmail, setShareEmail] = useState("");
  const [shareError, setShareError] = useState("");

  useEffect(() => {
    if (isAuthenticated) {
      getAccessTokenSilently().then((token) => {
        setAccessToken(token);
      });

      if (user && user.email) {
        setUserEmail(user.email);
      }
    }
  }, [isAuthenticated, getAccessTokenSilently, user]);

  const fetchDomains = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/user-domains-partner`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "X-User-Email": userEmail,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch domains");
      }

      const { domains } = await response.json();
      setDomains(domains);
      setLoading(false);
    } catch (error) {
      setError(`Error: ${error.message}`);
      setLoading(false);
      console.error("Error:", error);
    }
  }, [accessToken, userEmail, API_URL]);

  useEffect(() => {
    if (accessToken && userEmail) {
      fetchDomains();
    }
  }, [accessToken, userEmail, fetchDomains]);

  const getMaxRequestCount = (request_size) => {
    switch (request_size) {
      case "packone":
        return "10 000";
      case "packtwo":
        return "500 000";
      case "packthree":
        return "1 000 000";
      case "packfour":
        return "5 000 000";
      case "packfive":
        return "10 000 000";
      default:
        return "Unknown";
    }
  };

  const toggleSettingsMenu = (domain) => {
    setShowSettingsMenu((prev) => ({
      ...prev,
      [domain]: !prev[domain],
    }));
  };

  const showDeleteConfirmationPopup = (domain) => {
    setShowDeleteConfirmation(domain);
    setShowSettingsMenu((prev) => ({ ...prev, [domain]: false }));
  };

  const showUpgradePopupMenu = (domain) => {
    setShowUpgradePopup(domain);
    setShowSettingsMenu((prev) => ({ ...prev, [domain]: false }));
    setSelectedPackage(null);
  };

  const showUpgradeConfirmationPopup = (domain, pkg) => {
    setSelectedPackage(pkg);
    setShowUpgradePopup(null);
    setShowUpgradeConfirmation(domain);
  };

  const upgradeSubscription = async (domain) => {
    if (!accessToken || !userEmail || !selectedPackage) return;

    try {
      setUpgradingDomains((prev) => ({ ...prev, [domain]: true }));

      const response = await fetch(
        `${API_URL}/subscriptions/upgrade/${domain}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
            "X-User-Email": userEmail,
          },
          body: JSON.stringify({
            package_name: selectedPackage.package_name,
            title: selectedPackage.title,
            price_id: selectedPackage.stripeMonthlyPriceId,
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to upgrade subscription");
      }

      const result = await response.json();

      if (result.success) {
        setDomains((prev) =>
          prev.map((d) =>
            d.domain === domain
              ? {
                  ...d,
                  request_size: selectedPackage.package_name,
                }
              : d
          )
        );
        setShowUpgradeConfirmation(null);
        setSelectedPackage(null);
      } else {
        throw new Error(result.message || "Upgrade failed");
      }
    } catch (error) {
      setError(`Error: ${error.message}`);
      console.error("Error:", error);
    } finally {
      setUpgradingDomains((prev) => ({ ...prev, [domain]: false }));
    }
  };

  const formatNumber = (num) => {
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  };

  const deleteDomain = async (domain) => {
    if (isDeletingRef.current || !accessToken || !userEmail) return;
    isDeletingRef.current = true;

    try {
      setDeletingDomains((prev) => ({ ...prev, [domain]: true }));

      const response = await fetch(`${API_URL}/run-script2`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
          "X-User-Email": userEmail,
        },
        body: JSON.stringify({ domain }),
      });

      if (!response.ok) {
        throw new Error("Failed to delete domain");
      }

      const { jobId } = await response.json();
      await pollJobStatus(jobId, domain);
    } catch (error) {
      setError(`Error: ${error.message}`);
      console.error("Error:", error);
    } finally {
      isDeletingRef.current = false;
    }
  };

  const pollJobStatus = async (jobId, domain) => {
    try {
      const interval = setInterval(async () => {
        const response = await fetch(`${API_URL}/job-status/${jobId}`, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "X-User-Email": userEmail,
          },
        });

        if (!response.ok) {
          throw new Error("Failed to check job status");
        }

        const { status, error } = await response.json();

        if (status === "completed") {
          setDomains((prev) =>
            prev.map((d) => (d.domain === domain ? { ...d, deleted: true } : d))
          );
          clearInterval(interval);
          setDeletingDomains((prev) => ({ ...prev, [domain]: false }));
          setShowDeleteConfirmation(null);
          window.location.reload();
        } else if (status === "failed") {
          throw new Error(error || "Failed to delete domain");
        }
      }, 2000);
    } catch (error) {
      setError(`Error: ${error.message}`);
      console.error("Error:", error);
      setDeletingDomains((prev) => ({ ...prev, [domain]: false }));
    }
  };

  const showSharePopupHandler = (domain) => {
    setShowSharePopup(domain);
    setShowSettingsMenu((prev) => ({ ...prev, [domain]: false }));
    setShareEmail("");
    setShareError("");
  };

  const handleShareEmailChange = (e) => {
    setShareEmail(e.target.value);
    setShareError("");
  };

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const sanitizeEmail = (email) => {
    return email.trim().toLowerCase();
  };

  const shareDomain = async (domain) => {
    const sanitizedEmail = sanitizeEmail(shareEmail);

    if (!validateEmail(sanitizedEmail)) {
      setShareError("Please enter a valid email address");
      return;
    }

    try {
      const response = await fetch(`${API_URL}/partner-add`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
          "X-User-Email": userEmail,
        },
        body: JSON.stringify({
          domain,
          partnerEmail: sanitizedEmail,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to share domain");
      }

      setShareEmail("");
      setShareError("");

      setShareError({
        type: "success",
        message: `Delt ${domain} med ${sanitizedEmail}. Last inn siden på nytt for å se endringer.`,
      });
    } catch (error) {
      setShareError({
        type: "error",
        message: error.message,
      });
      console.error("Error sharing domain:", error);
    }
  };

  const removePartner = async (domain, partnerEmail) => {
    try {
      setRemovingPartners((prev) => ({
        ...prev,
        [`${domain}-${partnerEmail}`]: true,
      }));

      const response = await fetch(`${API_URL}/partner-remove`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
          "X-User-Email": userEmail,
        },
        body: JSON.stringify({
          domain,
          partnerEmail,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to remove partner");
      }

      setDomains((prevDomains) =>
        prevDomains.map((d) =>
          d.domain === domain
            ? {
                ...d,
                partner_emails: d.partner_emails.filter(
                  (e) => e !== partnerEmail
                ),
              }
            : d
        )
      );

      setShareError({
        type: "success",
        message: `Successfully removed ${partnerEmail} from ${domain}`,
      });
    } catch (error) {
      setShareError({
        type: "error",
        message: error.message,
      });
      console.error("Error removing partner:", error);
    } finally {
      setRemovingPartners((prev) => ({
        ...prev,
        [`${domain}-${partnerEmail}`]: false,
      }));
    }
  };

  return (
    <div className="user-domains">
      <h2>Administrer Dine servere:</h2>
      {loading ? (
        <PageLoader />
      ) : error ? (
        <p className="error">{error}</p>
      ) : domains.length === 0 ? (
        <p>Du administrerer ingen domener.</p>
      ) : (
        <ul>
          {domains.map(
            (
              {
                domain,
                status,
                requestCount,
                request_size,
                partner_emails,
                canUpgrade,
              },
              index
            ) => (
              <li key={index}>
                {domain}
                <span className={`status ${status}`}>{status}</span>
                <span className="request-count">
                  Forespørsler:{" "}
                  {requestCount !== undefined && request_size !== undefined
                    ? `${requestCount}/${getMaxRequestCount(request_size)}`
                    : "Loading..."}
                </span>
                <div className="settings-menu">
                  <button
                    className="settings-button"
                    onClick={() => toggleSettingsMenu(domain)}
                  >
                    &#8942;
                  </button>
                  {showSettingsMenu[domain] && (
                    <div className="settings-dropdown">
                      {canUpgrade && (
                        <button onClick={() => showUpgradePopupMenu(domain)}>
                          Oppgrader
                        </button>
                      )}
                      <button
                        onClick={() => showDeleteConfirmationPopup(domain)}
                        disabled={deletingDomains[domain]}
                      >
                        Slett
                      </button>
                      <button
                        onClick={() => showSharePopupHandler(domain)}
                        disabled={deletingDomains[domain]}
                      >
                        Del
                      </button>
                    </div>
                  )}
                </div>
                {showDeleteConfirmation === domain && (
                  <div className="delete-confirmation-popup">
                    <p>Er du sikker på at du vil slette {domain}?</p>
                    <button
                      onClick={() => deleteDomain(domain)}
                      disabled={deletingDomains[domain]}
                    >
                      {deletingDomains[domain] ? "Sletter..." : "Ja"}
                    </button>
                    <button onClick={() => setShowDeleteConfirmation(null)}>
                      Nei
                    </button>
                  </div>
                )}
                {showUpgradePopup === domain && (
                  <div className="upgrade-popup">
                    <h3>Oppgrader pakke for {domain}</h3>
                    <div className="package-options">
                      {packages.map((pkg, idx) => (
                        <div
                          key={idx}
                          className={`package-option ${
                            selectedPackage?.package_name === pkg.package_name
                              ? "selected"
                              : ""
                          }`}
                          onClick={() => setSelectedPackage(pkg)}
                        >
                          <h4>{pkg.title}</h4>
                          <p>{formatNumber(pkg.requests)} forespørsler</p>
                          <button
                            onClick={() =>
                              showUpgradeConfirmationPopup(domain, pkg)
                            }
                          >
                            Velg
                          </button>
                        </div>
                      ))}
                    </div>
                    <button
                      onClick={() => setShowUpgradePopup(null)}
                      className="close-button"
                    >
                      Lukk
                    </button>
                  </div>
                )}
                {showUpgradeConfirmation === domain && (
                  <div className="upgrade-confirmation-popup">
                    <p>Er du sikker på at du vil oppgradere pakken?</p>
                    {selectedPackage && (
                      <p className="package-details">
                        Ny pakke: {selectedPackage.title} (
                        {formatNumber(selectedPackage.requests)} forespørsler)
                      </p>
                    )}
                    <button
                      onClick={() => upgradeSubscription(domain)}
                      disabled={upgradingDomains[domain]}
                    >
                      {upgradingDomains[domain] ? "Oppgraderer..." : "Ja"}
                    </button>
                    <button
                      onClick={() => {
                        setShowUpgradeConfirmation(null);
                        setSelectedPackage(null);
                      }}
                      disabled={upgradingDomains[domain]}
                    >
                      Nei
                    </button>
                  </div>
                )}
                {showSharePopup === domain && (
                  <div className="share-popup">
                    <h3>Del {domain}</h3>
                    <p>Skriv inn e-postadresse:</p>
                    <input
                      type="email"
                      value={shareEmail}
                      onChange={handleShareEmailChange}
                      placeholder="partner@example.com"
                    />
                    {shareError && (
                      <div className={`message ${shareError.type || "error"}`}>
                        {shareError.message}
                        <button
                          onClick={() => setShareError(null)}
                          className="message-close"
                        >
                          ×
                        </button>
                      </div>
                    )}

                    <div className="popup-buttons">
                      <button
                        onClick={() => shareDomain(domain)}
                        disabled={!shareEmail || shareError?.type === "success"}
                      >
                        {shareError?.type === "success"
                          ? "Sendt!"
                          : "Send invitasjon"}
                      </button>
                      <button
                        onClick={() => {
                          setShowSharePopup(null);
                          setShareError(null);
                        }}
                      >
                        Avbryt
                      </button>
                    </div>
                    {partner_emails && partner_emails.length > 0 && (
                      <div className="partner-list">
                        <h4>Delt med:</h4>
                        <ul>
                          {partner_emails.map((email) => (
                            <li key={email}>
                              {email}
                              <button
                                onClick={() => removePartner(domain, email)}
                                disabled={
                                  removingPartners[`${domain}-${email}`]
                                }
                                className="remove-partner-btn"
                              >
                                {removingPartners[`${domain}-${email}`]
                                  ? "Fjerner..."
                                  : "Fjern"}
                              </button>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </li>
            )
          )}
        </ul>
      )}
    </div>
  );
};

export default UserDomainsPartner;
